/**
 * /periods — fiscal year + period management.
 *
 * For each fiscal year (newest first):
 *   - Year header with status, dates, "Year-end close" button (only when
 *     all 12 periods are CLOSED — locks the FY + posts the closing entries)
 *   - Grid of period cards (one per period_no): start/end dates, status
 *     chip, today highlight, action buttons appropriate to current status:
 *       open    → Close
 *       closed  → Reopen | Lock | FX revalue
 *       locked  → (no actions; terminal)
 *
 * Toolbar: "+ New fiscal year" creates the next year using the currently
 * configured period strategy + start month.
 *
 * All destructive actions go through ConfirmDialog with role-appropriate
 * warnings — Lock especially, because it's the only action that's truly
 * irreversible (the close_period_immutability trigger blocks any subsequent
 * status change).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Checkbox, MenuItem, Stack, TextField,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  closeChecklistQueryOptions,
  fiscalYearsListQueryOptions,
  glKeys,
  periodKeys,
  trialBalanceKeys,
} from "@/lib/queries";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toaster";
import type {
  CloseChecklist, CloseChecklistItem,
  FiscalPeriod, FiscalYear, PeriodStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/periods/")({
  component: PeriodsPage,
});

function todayIso() { return new Date().toISOString().slice(0, 10); }

function PeriodsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const yearsQuery = useQuery(fiscalYearsListQueryOptions());
  const [newYearOpen, setNewYearOpen] = useState(false);
  // Close-checklist dialog is a richer experience than the standard
  // ConfirmDialog (it polls /close-checklist and gates the button on
  // blockers), so it gets its own modal driven by a periodId state.
  const [closeDialogPeriod, setCloseDialogPeriod] = useState<FiscalPeriod | null>(null);
  const [closeThroughOpen, setCloseThroughOpen] = useState(false);

  // Sort years newest first
  const years = useMemo(() => {
    // Newest first by start date: a short transition year shares its number
    // with the full year after it (FISCAL-YEAR-CHANGE-001).
    return [...(yearsQuery.data ?? [])].sort((a, b) => b.start_date.localeCompare(a.start_date));
  }, [yearsQuery.data]);

  // ── Period action mutations (close / reopen / lock / fx-revalue) ─────────
  // Note: the "close" action goes through the close-checklist dialog
  // (CloseChecklistDialog) instead of this catch-all because closing a
  // period needs a pre-flight + force_warnings + notes — too rich for the
  // simple ConfirmDialog flow.
  const periodAction = useMutation({
    mutationFn: async (vars: { periodId: string; action: "reopen" | "lock" | "fx-revalue" }) =>
      api(`/api/periods/${vars.periodId}/${vars.action}`, { method: "POST" }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      // FX revalue posts a journal — nuke GL/TB caches too
      if (vars.action === "fx-revalue") {
        qc.invalidateQueries({ queryKey: glKeys.all });
        qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      }
      toast.success(`Period ${vars.action === "fx-revalue" ? "FX revalued" : vars.action + "d"}`);
    },
    onError: (err: Error) => toast.error(`Action failed: ${err.message}`),
  });

  const yearEndClose = useMutation({
    mutationFn: async (fyId: string) =>
      api(`/api/periods/years/${fyId}/year-end-close`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      toast.success("Year-end close posted");
    },
    onError: (err: Error) => toast.error(`Year-end close failed: ${err.message}`),
  });

  // Opens the rich CloseChecklistDialog instead of a plain confirm.
  // The dialog runs the pre-flight, surfaces blockers/warnings, and
  // posts the close itself.
  const requestClose = (p: FiscalPeriod) => {
    setCloseDialogPeriod(p);
  };

  const requestReopen = (p: FiscalPeriod) => {
    confirmAction({
      title: `Reopen period ${p.period_no}?`,
      message:
        `Period ${p.period_no} will go back to OPEN status.

` +
        `New backdated journals will be allowed in this period again. Use sparingly — ` +
        `repeated open/close cycles erode auditability.`,
      confirmLabel: "Reopen",
      kind: "primary",
      onConfirm: async () => {
        await periodAction.mutateAsync({ periodId: p.id, action: "reopen" });
      },
    });
  };

  const requestLock = (p: FiscalPeriod) => {
    confirmAction({
      title: `Lock period ${p.period_no}?`,
      message:
        `Period ${p.period_no} will be marked LOCKED.

` +
        `⚠ THIS IS IRREVERSIBLE.

` +
        `Once locked, no journal can ever be posted to this period again — including ` +
        `reversals. Lock only after the period is fully closed, audited, and you're sure ` +
        `nothing else will hit it. The DB trigger enforces this — there is no unlock.

` +
        `If you only want to prevent new entries temporarily, use Close instead.`,
      confirmLabel: "Lock permanently",
      kind: "danger",
      onConfirm: async () => {
        await periodAction.mutateAsync({ periodId: p.id, action: "lock" });
      },
    });
  };

  const requestFxRevalue = (p: FiscalPeriod) => {
    confirmAction({
      title: `Run FX revaluation on period ${p.period_no}?`,
      message:
        `Posts an FX_REVAL journal: revalues every non-base-currency monetary ` +
        `balance at the period-end exchange rate.

` +
        `The unrealized gain/loss hits the FX_GAIN / FX_LOSS system accounts.
` +
        `Safe to re-run; the agent will only post a delta.`,
      confirmLabel: "Run FX revaluation",
      kind: "primary",
      onConfirm: async () => {
        await periodAction.mutateAsync({ periodId: p.id, action: "fx-revalue" });
      },
    });
  };

  const requestYearEndClose = (fy: FiscalYear) => {
    confirmAction({
      title: `Year-end close FY${fy.year}${fy.is_transition ? " (short year)" : ""}?`,
      message:
        `Posts the year-end close entries:
` +
        `  1. Roll all P&L (revenue + expense) into Income Summary
` +
        `  2. Roll Income Summary into Retained Earnings
` +
        `  3. Lock all 12 periods
` +
        `  4. Lock the fiscal year itself

` +
        `Requires every monthly period to already be CLOSED.

` +
        `⚠ The fiscal year is permanently locked after this. Roll the next FY ` +
        `with "+ New fiscal year" before closing if you need to keep posting.`,
      confirmLabel: "Run year-end close",
      kind: "danger",
      onConfirm: async () => {
        await yearEndClose.mutateAsync(fy.id);
      },
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Fiscal Periods</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {years.length} fiscal year{years.length === 1 ? "" : "s"} · open periods accept journals
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setCloseThroughOpen(true)}
            className="text-sm border border-slate-300 dark:border-gray-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800 rounded-md px-3 py-1.5 font-medium"
          >
            Close periods through a date…
          </button>
          <button
            type="button"
            onClick={() => setNewYearOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New fiscal year
          </button>
        </div>
      </div>

      {/* Years list */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        {yearsQuery.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}
        {yearsQuery.error && (
          <div className="text-sm text-red-600">
            Error: {(yearsQuery.error as Error).message}
          </div>
        )}
        {years.length === 0 && !yearsQuery.isLoading && (
          <div className="text-sm text-slate-400 italic dark:text-slate-500">
            No fiscal years yet — click <strong>+ New fiscal year</strong> to start.
          </div>
        )}
        {years.map((fy) => (
          <YearCard
            key={fy.id}
            fy={fy}
            onClose={requestClose}
            onReopen={requestReopen}
            onLock={requestLock}
            onFxRevalue={requestFxRevalue}
            onYearEnd={requestYearEndClose}
            yearEndPending={yearEndClose.isPending}
            actionPending={periodAction.isPending}
          />
        ))}
      </div>

      <NewFiscalYearDialog open={newYearOpen} onClose={() => setNewYearOpen(false)} />
      <CloseChecklistDialog
        period={closeDialogPeriod}
        onClose={() => setCloseDialogPeriod(null)}
      />
      <CloseThroughDialog open={closeThroughOpen} onClose={() => setCloseThroughOpen(false)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Close every open period through a date (FIX-LOG 101). The person picks
// the date; the preview lists each period with its own checklist; the
// apply closes exactly what the preview showed.
// ─────────────────────────────────────────────────────────────
type CloseThroughPeriod = {
  period_id: string; fiscal_year: number; period_no: number;
  start_date: string; end_date: string; blockers: string[]; warnings: string[];
};
type CloseThroughPreview = {
  through_date: string; imported_through: string | null;
  periods: CloseThroughPeriod[]; period_count: number;
  blocked: CloseThroughPeriod[]; with_warnings: number; plan_token: string;
};

function CloseThroughDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [through, setThrough] = useState("");
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const [plan, setPlan] = useState<CloseThroughPreview | null>(null);

  useEffect(() => {
    if (open) { setThrough(""); setAcceptWarnings(false); setPlan(null); }
  }, [open]);

  const previewMutation = useMutation({
    mutationFn: (d: string) =>
      api<CloseThroughPreview>("/api/periods/close-through/preview", {
        method: "POST", body: JSON.stringify({ through_date: d }),
      }),
    onSuccess: (p) => { setPlan(p); setAcceptWarnings(false); },
    onError: (err: Error) => { setPlan(null); toast.error(err.message); },
  });
  const applyMutation = useMutation({
    mutationFn: () =>
      api<{ closed_count: number }>("/api/periods/close-through", {
        method: "POST",
        body: JSON.stringify({
          through_date: plan!.through_date, plan_token: plan!.plan_token,
          accept_warnings: acceptWarnings,
        }),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      toast.success(`${r.closed_count} period${r.closed_count === 1 ? "" : "s"} closed through ${plan!.through_date}`);
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message);
      if (through) previewMutation.mutate(through);
    },
  });

  const blocked = (plan?.blocked.length ?? 0) > 0;
  const needsAccept = (plan?.with_warnings ?? 0) > 0;
  const canApply = !!plan && plan.period_count > 0 && !blocked && (!needsAccept || acceptWarnings)
    && !applyMutation.isPending;

  return (
    <Dialog open={open} onClose={applyMutation.isPending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Close periods through a date</DialogTitle>
      <DialogContent dividers>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
          Closes every open period that ends on or before the date you choose, oldest first, with the same
          checklist, trial-balance snapshot and audit record as closing one period. Closed periods take no new
          journals until reopened. Nothing is posted; year-end close stays its own step.
        </p>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            type="date" size="small" label="Close through"
            InputLabelProps={{ shrink: true }}
            value={through}
            onChange={(e) => { setThrough(e.target.value); setPlan(null); }}
          />
          <Button
            variant="outlined"
            disabled={!through || previewMutation.isPending}
            onClick={() => previewMutation.mutate(through)}
          >
            {previewMutation.isPending ? "Checking…" : "Show what would close"}
          </Button>
        </Stack>
        {plan && (
          <div className="space-y-2 text-sm">
            {plan.imported_through && (
              <p className="text-slate-600 dark:text-slate-300">
                Imported QuickBooks history runs through <strong>{plan.imported_through}</strong>.
              </p>
            )}
            {plan.period_count === 0 ? (
              <p className="text-slate-600 dark:text-slate-300">
                No open period ends on or before {plan.through_date}. Choose a later date, or close
                periods one at a time below.
              </p>
            ) : (
              <p className="text-slate-700 dark:text-slate-200">
                {plan.period_count} open period{plan.period_count === 1 ? "" : "s"} would close
                ({plan.periods[0].start_date} to {plan.periods[plan.periods.length - 1].end_date}).
              </p>
            )}
            {blocked && (
              <p className="text-red-600">
                {plan.blocked.length} period{plan.blocked.length === 1 ? " has" : "s have"} blockers, so nothing
                can close until they are fixed (listed below).
              </p>
            )}
            <div className="max-h-72 overflow-auto border border-slate-200 dark:border-gray-800 rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-gray-800">
                    <th className="px-2 py-1">Period</th><th className="px-2 py-1">Dates</th>
                    <th className="px-2 py-1">Checklist</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.periods.map((p) => (
                    <tr key={p.period_id} className="border-b border-slate-100 dark:border-gray-800 align-top">
                      <td className="px-2 py-1 whitespace-nowrap">FY{p.fiscal_year} P{p.period_no}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{p.start_date} – {p.end_date}</td>
                      <td className="px-2 py-1">
                        {p.blockers.map((b) => <div key={b} className="text-red-600">Blocker: {b}</div>)}
                        {p.warnings.map((w) => <div key={w} className="text-amber-700 dark:text-amber-400">Warning: {w}</div>)}
                        {!p.blockers.length && !p.warnings.length && <span className="text-emerald-700">Ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {needsAccept && !blocked && (
              <FormControlLabel
                control={<Checkbox checked={acceptWarnings} onChange={(e) => setAcceptWarnings(e.target.checked)} />}
                label={`Close anyway: ${plan.with_warnings} period${plan.with_warnings === 1 ? " has" : "s have"} warnings I have reviewed`}
              />
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applyMutation.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!canApply} onClick={() => applyMutation.mutate()}>
          {applyMutation.isPending
            ? "Closing…"
            : plan && plan.period_count > 0
              ? `Close ${plan.period_count} period${plan.period_count === 1 ? "" : "s"}`
              : "Close periods"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// One fiscal year — header + grid of period cards
// ─────────────────────────────────────────────────────────────
function YearCard({
  fy, onClose, onReopen, onLock, onFxRevalue, onYearEnd,
  yearEndPending, actionPending,
}: {
  fy: FiscalYear;
  onClose: (p: FiscalPeriod) => void;
  onReopen: (p: FiscalPeriod) => void;
  onLock: (p: FiscalPeriod) => void;
  onFxRevalue: (p: FiscalPeriod) => void;
  onYearEnd: (fy: FiscalYear) => void;
  yearEndPending: boolean;
  actionPending: boolean;
}) {
  const today = todayIso();
  const periods = [...fy.periods].sort((a, b) => a.period_no - b.period_no);
  const allClosed = periods.length > 0 && periods.every((p) => p.status === "closed");
  const anyLocked = periods.some((p) => p.status === "locked");
  const fyLocked = fy.status === "locked";

  // Stats
  const counts = periods.reduce(
    (acc, p) => {
      acc[p.status] = (acc[p.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
      {/* Year header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          FY{fy.year}{fy.is_transition ? " (short year)" : ""}
        </h2>
        <StatusChip status={fy.status} />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {fy.start_date} → {fy.end_date} · {periods.length} periods
          {counts.open ? ` · ${counts.open} open` : ""}
          {counts.closed ? ` · ${counts.closed} closed` : ""}
          {counts.locked ? ` · ${counts.locked} locked` : ""}
        </span>
        <div className="flex-1" />
        {!fyLocked && allClosed && (
          <button
            type="button"
            onClick={() => onYearEnd(fy)}
            disabled={yearEndPending}
            className="text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
            title="Run year-end close: roll P&L → Retained Earnings, lock the FY"
          >
            {yearEndPending ? "Closing…" : "🔒 Year-end close"}
          </button>
        )}
        {fyLocked && (
          <span className="text-xs text-red-700 font-medium">
            ✓ Fiscal year locked
          </span>
        )}
      </div>

      {/* Period grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 p-4">
        {periods.map((p) => (
          <PeriodCard
            key={p.id}
            period={p}
            isCurrent={today >= p.start_date && today <= p.end_date}
            anyLocked={anyLocked}
            disabled={actionPending}
            onClose={() => onClose(p)}
            onReopen={() => onReopen(p)}
            onLock={() => onLock(p)}
            onFxRevalue={() => onFxRevalue(p)}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// One period card
// ─────────────────────────────────────────────────────────────
function PeriodCard({
  period, isCurrent, anyLocked, disabled,
  onClose, onReopen, onLock, onFxRevalue,
}: {
  period: FiscalPeriod;
  isCurrent: boolean;
  anyLocked: boolean;          // unused; reserved for future "all prior periods locked" hint
  disabled: boolean;
  onClose: () => void;
  onReopen: () => void;
  onLock: () => void;
  onFxRevalue: () => void;
}) {
  void anyLocked;
  const ringClass =
    isCurrent ? "ring-2 ring-blue-400 border-blue-300"
      : period.status === "open" ? "border-slate-200"
      : period.status === "closed" ? "border-amber-200 bg-amber-50/30"
      : "border-red-200 bg-red-50/30";
  return (
    <div className={`border rounded-md p-3 ${ringClass} flex flex-col gap-2`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-sm font-semibold">P{period.period_no}</div>
        <StatusChip status={period.status} small />
      </div>
      <div className="text-xs text-slate-500 font-mono dark:text-slate-400">
        {period.start_date} → {period.end_date}
      </div>
      {isCurrent && (
        <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">
          ◆ current
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-1">
        {period.status === "open" && (
          <ActionBtn label="Close" onClick={onClose} disabled={disabled} />
        )}
        {period.status === "closed" && (
          <>
            <ActionBtn label="Reopen" onClick={onReopen} disabled={disabled} />
            <ActionBtn label="Lock" onClick={onLock} disabled={disabled} danger />
            <ActionBtn label="FX rev" onClick={onFxRevalue} disabled={disabled} subtle />
          </>
        )}
        {period.status === "locked" && (
          <span className="text-[10px] text-red-700 italic">no actions (locked)</span>
        )}
      </div>
    </div>
  );
}

function ActionBtn({
  label, onClick, disabled, danger, subtle,
}: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean; subtle?: boolean }) {
  const cls =
    danger
      ? "bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
      : subtle
      ? "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
      : "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-[11px] px-2 py-0.5 rounded border ${cls} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Status chip
// ─────────────────────────────────────────────────────────────
function StatusChip({ status, small }: { status: PeriodStatus; small?: boolean }) {
  const cls =
    status === "open" ? "bg-emerald-100 text-emerald-800"
      : status === "closed" ? "bg-amber-100 text-amber-800"
      : "bg-red-100 text-red-800";
  return (
    <span
      className={`uppercase tracking-wide font-semibold rounded ${cls}`}
      style={{ fontSize: small ? 9 : 10, padding: small ? "1px 5px" : "2px 6px" }}
    >
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// New fiscal year dialog
// ─────────────────────────────────────────────────────────────
function NewFiscalYearDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  // Default to the year after the latest existing one
  const yearsQuery = useQuery(fiscalYearsListQueryOptions());
  const defaultYear = useMemo(() => {
    const latest = (yearsQuery.data ?? []).reduce(
      (max, y) => Math.max(max, y.year), 0,
    );
    return latest > 0 ? latest + 1 : new Date().getFullYear();
  }, [yearsQuery.data]);

  const [year, setYear] = useState<number>(defaultYear);
  const [startDate, setStartDate] = useState(`${defaultYear}-01-01`);
  const [endDate, setEndDate] = useState(`${defaultYear}-12-31`);
  const [strategy, setStrategy] = useState<string>("monthly");

  // Re-init when the dialog opens or defaultYear changes (years query loads)
  useEffect(() => {
    if (open) {
      setYear(defaultYear);
      setStartDate(`${defaultYear}-01-01`);
      setEndDate(`${defaultYear}-12-31`);
      setStrategy("monthly");
    }
  }, [open, defaultYear]);

  const create = useMutation({
    mutationFn: async () =>
      api("/api/periods/years", {
        method: "POST",
        body: { year, start_date: startDate, end_date: endDate, strategy },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      toast.success(`FY${year} created (${strategy})`);
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16 }}>New fiscal year</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Year"
            type="number"
            size="small"
            value={year}
            onChange={(e) => {
              const y = Number(e.target.value);
              setYear(y);
              setStartDate(`${y}-01-01`);
              setEndDate(`${y}-12-31`);
            }}
            inputProps={{ min: 2000, max: 2200 }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="End date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Period strategy"
            select
            size="small"
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            helperText={STRATEGY_DESC[strategy] ?? ""}
          >
            <MenuItem value="monthly">Monthly — 12 calendar months</MenuItem>
            <MenuItem value="thirteen_four_week">
              13 × 4-week — 13 × 28-day periods
            </MenuItem>
            <MenuItem value="four_four_five">
              4-4-5 retail — NRF retail (12 periods, 4-4-5 weeks per quarter)
            </MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => create.mutate()}
          disabled={create.isPending}
        >
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const STRATEGY_DESC: Record<string, string> = {
  monthly:
    "12 periods, each = one calendar month. SaaS / professional services / most businesses.",
  thirteen_four_week:
    "13 periods × 28 days each. Manufacturing / process industries — equal business days per period.",
  four_four_five:
    "12 periods (3 per quarter, weeks of 4-4-5 each = 13 weeks/quarter × 4 = 52 weeks). NRF retail standard.",
};


// ─────────────────────────────────────────────────────────────
// CloseChecklistDialog
// Pre-flight + close in one modal. Mirrors the `close_checklist`
// service: blockers must be cleared; warnings can be overridden via
// the force_warnings checkbox; notes are stored in period_close_runs.
// ─────────────────────────────────────────────────────────────
function CloseChecklistDialog({
  period, onClose,
}: { period: FiscalPeriod | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [forceWarnings, setForceWarnings] = useState(false);
  const [notes, setNotes] = useState("");

  // Re-init each time the dialog opens for a new period
  useEffect(() => {
    if (period) {
      setForceWarnings(false);
      setNotes("");
    }
  }, [period?.id]);

  const checklistQuery = useQuery({
    ...closeChecklistQueryOptions(period?.id ?? null),
    // Always re-fetch when the dialog opens — stale data here would
    // mislead the user about what's blocking close.
    refetchOnMount: "always",
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      if (forceWarnings) params.set("force_warnings", "true");
      if (notes.trim()) params.set("notes", notes.trim());
      const qs = params.toString();
      return api(
        `/api/periods/${period!.id}/close${qs ? `?${qs}` : ""}`,
        { method: "POST" },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: periodKeys.all });
      qc.invalidateQueries({ queryKey: glKeys.all });
      qc.invalidateQueries({ queryKey: trialBalanceKeys.all });
      toast.success(`Period ${period!.period_no} closed`);
      onClose();
    },
    onError: (err: Error) => {
      // The backend rejects on hard blockers AND on un-forced warnings —
      // surface the message verbatim and refetch so the panel updates.
      toast.error(`Close failed: ${err.message}`);
      checklistQuery.refetch();
    },
  });

  if (!period) return null;
  const checklist: CloseChecklist | undefined = checklistQuery.data;
  const blockers = checklist?.blockers ?? [];
  const warnings = checklist?.warnings ?? [];
  const hasBlockers = blockers.length > 0;
  const hasWarnings = warnings.length > 0;
  // Backend will reject if warnings exist and force_warnings=false
  const canSubmit = !hasBlockers && (!hasWarnings || forceWarnings) && !closeMutation.isPending;

  return (
    <Dialog open={Boolean(period)} onClose={closeMutation.isPending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontSize: 16 }}>
        Close period P{period.period_no}
        <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
          {period.start_date} → {period.end_date}
        </span>
      </DialogTitle>
      <DialogContent dividers>
        {checklistQuery.isLoading && (
          <div className="text-sm text-slate-500 py-4 dark:text-slate-400">Running pre-flight checks…</div>
        )}
        {checklistQuery.error && (
          <div className="text-sm text-red-700 py-4">
            Failed to load checklist: {(checklistQuery.error as Error).message}
          </div>
        )}
        {checklist && (
          <div className="flex flex-col gap-4">
            {/* Stats — always shown, gives context on what's in the period */}
            <ChecklistStatsRow stats={checklist.stats} />

            {/* Blockers — must be cleared. Hard stop. */}
            <ChecklistSection
              title="Blockers"
              emptyMessage="No blockers — period is safe to close."
              items={blockers}
              tone="error"
            />

            {/* Warnings — overridable with force_warnings */}
            <ChecklistSection
              title="Warnings"
              emptyMessage="No warnings."
              items={warnings}
              tone="warning"
            />

            {/* Override + notes inputs */}
            {hasWarnings && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceWarnings}
                    onChange={(e) => setForceWarnings(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <span className="text-sm">
                    Acknowledge warnings and proceed anyway (recorded in audit log)
                  </span>
                }
                sx={{ alignSelf: "flex-start" }}
              />
            )}
            <TextField
              label="Notes (optional)"
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this period is being closed, who reviewed, exceptions, etc."
              helperText="Stored on the period_close_runs audit row."
            />

            {hasBlockers && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                ⚠ Resolve all blockers before closing. The DB triggers reject the
                close otherwise. Reopening or backfilling those journals/lines
                first is the typical fix.
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={closeMutation.isPending}>Cancel</Button>
        <Button onClick={() => checklistQuery.refetch()} disabled={checklistQuery.isFetching}>
          {checklistQuery.isFetching ? "Re-checking…" : "Re-run checks"}
        </Button>
        <Button
          variant="contained"
          color={hasWarnings ? "warning" : "primary"}
          onClick={() => closeMutation.mutate()}
          disabled={!canSubmit}
        >
          {closeMutation.isPending
            ? "Closing…"
            : hasWarnings && forceWarnings
            ? "Close with overrides"
            : "Close period"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChecklistStatsRow({ stats }: { stats: CloseChecklist["stats"] }) {
  const items: Array<{ label: string; value: string | number | undefined }> = [
    { label: "Period", value: stats.period_no ? `P${stats.period_no}` : undefined },
    { label: "Posted journals", value: stats.posted_journal_count as number | undefined },
    { label: "Draft journals", value: stats.draft_journal_count as number | undefined },
  ];
  return (
    <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded p-3 dark:bg-gray-950 dark:border-gray-800">
      {items.map((it) => (
        <div key={it.label}>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{it.label}</div>
          <div className="text-sm font-mono font-semibold text-slate-800 mt-0.5 dark:text-slate-200">
            {it.value ?? "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChecklistSection({
  title, items, emptyMessage, tone,
}: {
  title: string;
  items: CloseChecklistItem[];
  emptyMessage: string;
  tone: "error" | "warning";
}) {
  const empty = items.length === 0;
  const headerCls = tone === "error" ? "text-red-700" : "text-amber-700";
  const itemCls =
    tone === "error"
      ? "border-red-200 bg-red-50/50"
      : "border-amber-200 bg-amber-50/50";
  return (
    <div>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${headerCls}`}>
        {title}
        {!empty && (
          <span className="ml-2 inline-block text-[10px] bg-white px-1.5 py-0.5 rounded border border-current dark:bg-gray-900">
            {items.length}
          </span>
        )}
      </div>
      {empty ? (
        <div className="text-xs text-slate-500 italic dark:text-slate-400">{emptyMessage}</div>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li
              key={it.code}
              className={`text-sm border rounded px-2 py-1.5 ${itemCls}`}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] text-slate-500 uppercase dark:text-slate-400">
                  {it.code}
                </span>
                <span className="flex-1 text-slate-800 dark:text-slate-200">{it.message}</span>
                {typeof it.count === "number" && (
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                    × {it.count}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
